import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useChat } from '@/hooks';
import { useAssistants } from '@/hooks/useAssistants';
import { ChatInput } from '../components/chat/ChatInput';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { EmptyState } from '../components/chat/EmptyState';
import { LoadingState } from '../components/common/LoadingState';
import { EmptyAssistantsState } from '../components/common/EmptyAssistantState';
import { CanonicalTag } from '@/components/common/CanonicalTag';
import { Building2, Car, Search, ArrowLeftRight, HelpCircle, Wrench, Home, DollarSign } from 'lucide-react';
import { gtmService } from '@/services/retargeting/gtm.service';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';

interface ChatPageProps {
    showBanner: () => void;
}

export const ChatPage: React.FC<ChatPageProps> = ({ showBanner }) => {
    const {
        messages,
        isLoading,
        streamingMessage,
        selectedAssistant,
        sendMessage,
        setSelectedAssistant,
        startNewChat,
        currentTool,
    } = useChat();

    const { assistants, loading: loadingAssistants } = useAssistants();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [hasTrackedFirstMessage, setHasTrackedFirstMessage] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const pendingQuery = searchParams.get('q');

    const assistant = assistants[selectedAssistant] || null;

    // Auto-select the assistant on load
    useEffect(() => {
        if (!loadingAssistants && !selectedAssistant) {
            const assistantIds = Object.keys(assistants);
            if (assistantIds.length > 0) {
                const firstAssistantId = assistantIds[0];
                setSelectedAssistant(firstAssistantId);
                startNewChat();
            }
        }
    }, [loadingAssistants, assistants, selectedAssistant, setSelectedAssistant, startNewChat]);

    // Auto-send message from ?q= query param (e.g. from FAQ index page prompts)
    useEffect(() => {
        if (pendingQuery && assistant && messages.length === 0) {
            setSearchParams({}, { replace: true });
            sendMessage(pendingQuery);
        }
    }, [pendingQuery, assistant]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    const handlePromptClick = (prompt: string) => {
        if (!hasTrackedFirstMessage) {
            gtmService.trackFirstMessage(assistant?.name);
            fbPixelService.trackChatStarted();
            setHasTrackedFirstMessage(true);
        }

        sendMessage(prompt);
    };

    // Loading state
    if (loadingAssistants) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading assistants..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // No assistants available
    if (Object.keys(assistants).length === 0) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <EmptyAssistantsState />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Show loading state while assistant is being auto-selected
    if (!assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading assistant..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const hasMessages = messages.length > 0;

    // Chat page with selected assistant
    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/" />
            <Header />
            <div className={`${hasMessages ? 'pt-16 sm:pt-20 md:pt-24 pb-28 sm:pb-32 md:pb-36' : 'flex flex-col justify-center min-h-[calc(100vh-56px)] py-6'} px-2 sm:px-3 md:px-4`}>
                <div className="max-w-3xl mx-auto">
                    <div className={`${hasMessages ? 'min-h-[60vh] py-2 sm:py-3 md:py-4' : ''}`}>
                        {hasMessages ? (
                            <div className="space-y-2 sm:space-y-3 md:space-y-4">
                                <ChatContainer
                                    messages={messages}
                                    assistant={assistant}
                                    isLoading={isLoading}
                                    streamingMessage={streamingMessage}
                                    currentTool={currentTool}
                                />
                            </div>
                        ) : (
                            <>
                                <EmptyState
                                    assistant={assistant}
                                    onPromptClick={handlePromptClick}
                                    compact
                                />
                                <ChatInput
                                    assistant={assistant}
                                    onSendMessage={sendMessage}
                                    disabled={isLoading}
                                    showBanner={showBanner}
                                    firstMessageTracked={hasTrackedFirstMessage}
                                    inline
                                />
                                {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME === 'DriveSource' && (
                                    <>
                                        <div className="flex items-center gap-3 mt-8 sm:mt-10 md:mt-12">
                                            <div className="flex-1 h-px bg-gray-700/60" />
                                            <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                                Explore {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME}
                                            </span>
                                            <div className="flex-1 h-px bg-gray-700/60" />
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5 mt-3">
                                            {[
                                                { label: 'Dealerships', to: '/dealerships', Icon: Building2 },
                                                { label: 'Vehicles', to: '/vehicles', Icon: Car },
                                                { label: 'Research', to: '/research', Icon: Search },
                                                { label: 'Comparisons', to: '/compare', Icon: ArrowLeftRight },
                                                { label: 'FAQ', to: '/faq', Icon: HelpCircle },
                                                { label: 'Services', to: '/services', Icon: Wrench },
                                            ].map(({ label, to, Icon }) => (
                                                <Link
                                                    key={to}
                                                    to={to}
                                                    className="flex items-center gap-2 sm:gap-2.5 p-3 rounded-lg border border-gray-700/60 bg-[#1a1a1a] hover:border-gray-600 hover:bg-[#222] transition-colors min-h-[44px]"
                                                >
                                                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#111] border border-gray-700/40 flex items-center justify-center flex-shrink-0">
                                                        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-400" />
                                                    </div>
                                                    <span className="text-xs sm:text-sm font-medium text-white">{label}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME === 'HomeSource' && (
                                    <>
                                        <div className="flex items-center gap-3 mt-8 sm:mt-10 md:mt-12">
                                            <div className="flex-1 h-px bg-gray-700/60" />
                                            <span className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                                Explore HomeSource
                                            </span>
                                            <div className="flex-1 h-px bg-gray-700/60" />
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2.5 mt-3">
                                            {[
                                                { label: 'Home Affordability', to: '/faq/how-much-house-can-i-afford', Icon: Home },
                                                { label: 'Closing Costs', to: '/faq/home-buying-closing-costs', Icon: DollarSign },
                                                { label: 'Buy vs Rent', to: '/faq/buy-vs-rent-home', Icon: ArrowLeftRight },
                                            ].map(({ label, to, Icon }) => (
                                                <Link
                                                    key={to}
                                                    to={to}
                                                    className="flex items-center gap-2 sm:gap-2.5 p-3 rounded-lg border border-gray-700/60 bg-[#1a1a1a] hover:border-gray-600 hover:bg-[#222] transition-colors min-h-[44px]"
                                                >
                                                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#111] border border-gray-700/40 flex items-center justify-center flex-shrink-0">
                                                        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-cyan-400" />
                                                    </div>
                                                    <span className="text-xs sm:text-sm font-medium text-white">{label}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            {hasMessages ? (
                <ChatInput
                    assistant={assistant}
                    onSendMessage={sendMessage}
                    disabled={isLoading}
                    showBanner={showBanner}
                    firstMessageTracked={hasTrackedFirstMessage}
                />
            ) : (
                <Footer />
            )}
        </>
    );
};